import { useEffect, useState, useCallback } from "react";
import api, { MAD } from "../lib/api";
import { PageHeader, ExportButtons, StatCard, Loader, Empty } from "../components/ui-kit";
import { PeriodFilter, buildPeriodQuery } from "../components/PeriodFilter";
import { exportExcel, exportPDF } from "../lib/exporters";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend } from "recharts";

const GOLD = "#D4AF37", GREEN = "#4CAF50", RED = "#F44336";
const tipStyle = { background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#EDE6D6" };

const TABS = [
  { key: "global", label: "Global société" },
  { key: "boutique", label: "Boutique principale" },
  { key: "stand", label: "Stand Foire" },
  { key: "b2b", label: "Distribution B2B" },
  { key: "platforms", label: "Livraison (Glovo)" },
  { key: "labo", label: "Laboratoire / Production" },
  { key: "produits", label: "Produits & marges" },
  { key: "depenses", label: "Dépenses" },
  { key: "retours", label: "Retours & pertes" },
  { key: "stock", label: "Stock ingrédients" },
  { key: "freezer", label: "Stock Congélateur" },
  { key: "production", label: "Production" },
];

function Evo({ ev }) {
  if (!ev || ev.pct == null) return <span className="text-[var(--tm-muted)] text-xs">—</span>;
  const up = ev.trend === "up", down = ev.trend === "down";
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const c = up ? GREEN : down ? RED : "var(--tm-muted)";
  return <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: c }}><Icon size={13} />{ev.pct > 0 ? "+" : ""}{ev.pct}%</span>;
}

function inRange(dstr, start, end) {
  if (!dstr) return false;
  const d = new Date(dstr); const s = new Date(start), e = new Date(end);
  e.setHours(23, 59, 59); return d >= s && d <= e;
}

function Metrics({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6" data-testid="report-metrics">
      {items.map((it, i) => (
        <StatCard key={i} label={it.label} value={it.value} accent={it.accent} sub={it.sub} testid={`metric-${i}`} />
      ))}
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("global");
  const [q, setQ] = useState({ period: "month" });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extra, setExtra] = useState({ products: [], stock: [], prodReport: null, summary: null, expenses: [], retours: [], destructions: [], freezer: [] });

  const load = useCallback((query) => {
    setLoading(true);
    api.get(`/overview?${buildPeriodQuery(query)}`).then(({ data }) => setD(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(q); }, [load]); // eslint-disable-line
  useEffect(() => {
    Promise.all([
      api.get("/products").catch(() => ({ data: [] })),
      api.get("/stock").catch(() => ({ data: [] })),
      api.get("/productions/report").catch(() => ({ data: null })),
      api.get("/distribution/summary").catch(() => ({ data: null })),
      api.get("/expenses").catch(() => ({ data: [] })),
      api.get("/distribution/retours").catch(() => ({ data: [] })),
      api.get("/destructions").catch(() => ({ data: [] })),
      api.get("/freezer").catch(() => ({ data: [] })),
    ]).then(([p, s, pr, su, ex, re, de, fz]) => setExtra({ products: p.data, stock: s.data, prodReport: pr.data, summary: su.data, expenses: ex.data, retours: re.data, destructions: de.data, freezer: fz.data }));
  }, []);

  const onPeriod = (next) => { setQ(next); load(next); };

  if (loading || !d) return (<div><PageHeader title="Rapports" subtitle="Analyse complète de l'activité." /><PeriodFilter value={q} onChange={onPeriod} /><Loader /></div>);

  const g = d.global, ev = d.comparaison.evolution;
  const start = d.periode.start, end = d.periode.end;
  const findDept = (matcher) => d.departements.find(matcher);
  const boutiquePrincipale = findDept((x) => x.type === "boutique" && /principale/i.test(x.nom));
  const standFoire = findDept((x) => x.type === "boutique" && /stand|foire/i.test(x.nom));

  // --- Export config per tab ---
  let exportName = "rapport", exportTitle = "Rapport TIRAMESSI", exportCols = [], exportRows = [];

  const deptMetrics = (dep) => dep ? [
    { label: "CA brut", value: MAD(dep.ca ?? dep.ca_brut ?? 0), accent: GOLD },
    { label: "CA net", value: MAD(dep.ca_net ?? dep.ca ?? 0) },
    { label: "Dépenses", value: MAD(dep.depenses), accent: RED },
    { label: "Pertes", value: MAD(dep.pertes ?? 0), accent: RED },
    { label: "Bénéfice brut", value: MAD(dep.benefice ?? dep.marge_brut ?? 0) },
    { label: "Bénéfice net", value: MAD(dep.benefice_net), accent: dep.benefice_net >= 0 ? GREEN : RED },
    { label: "Marge %", value: `${dep.marge_pct}%` },
    { label: "Ventes", value: dep.nb_ventes ?? dep.nb_livraisons ?? 0 },
    { label: "Panier moyen", value: MAD(dep.panier_moyen ?? 0) },
  ] : [];

  let body = null;

  const TopProduitsCard = ({ title = "Produits les plus vendus", rows }) => {
    const list = rows ?? d.top_vendus;
    return (
    <div className="tm-card p-5" data-testid="report-top-vendus">
      <h3 className="font-display text-xl mb-4">{title}</h3>
      {(!list || list.length === 0) ? <Empty text="Aucune vente sur la période." /> : (
        <table className="tm-table"><thead><tr><th>Produit</th><th className="text-right">Qté</th></tr></thead>
          <tbody>{list.map((p, i) => <tr key={i}><td>{p.nom}</td><td className="text-right tabular">{p.quantite}</td></tr>)}</tbody></table>
      )}
    </div>
    );
  };

  const ComparisonCard = () => (
    <div className="tm-card p-5 mb-6" data-testid="report-comparison">
      <h3 className="font-display text-xl mb-1">Comparaison des périodes</h3>
      <p className="text-xs text-[var(--tm-muted)] mb-4">{d.comparaison.label} vs {d.comparaison.label_prec}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[["ca_brut", "CA brut"], ["ca_net", "CA net"], ["depenses", "Dépenses"], ["benefice_net", "Bénéfice net"], ["nb_ventes", "Ventes"]].map(([k, lbl]) => (
          <div key={k} className="tm-card p-3" style={{ background: "var(--tm-bg)" }}>
            <p className="text-[11px] uppercase tracking-wider text-[var(--tm-muted)]">{lbl}</p>
            <p className="font-display text-lg tabular mt-1">{k === "nb_ventes" ? d.comparaison.actuel[k] : MAD(d.comparaison.actuel[k])}</p>
            <div className="flex items-center justify-between mt-1"><span className="text-[11px] text-[var(--tm-muted)]">{k === "nb_ventes" ? d.comparaison.precedent[k] : MAD(d.comparaison.precedent[k])}</span><Evo ev={ev[k]} /></div>
          </div>
        ))}
      </div>
    </div>
  );

  if (tab === "global") {
    exportName = "rapport_global"; exportTitle = `Rapport global société — ${d.periode.label}`;
    exportCols = [{ header: "Département", value: (r) => r.nom }, { header: "CA", value: (r) => r.ca ?? r.ca_brut ?? 0 }, { header: "Dépenses", value: (r) => r.depenses }, { header: "Bénéfice net", value: (r) => r.benefice_net }];
    exportRows = d.departements;
    body = (
      <>
        <Metrics items={[
          { label: "CA global brut", value: MAD(g.ca_brut), accent: GOLD, sub: <Evo ev={ev.ca_brut} /> },
          { label: "CA net", value: MAD(g.ca_net), sub: <Evo ev={ev.ca_net} /> },
          { label: "Dépenses", value: MAD(g.depenses), accent: RED, sub: <Evo ev={ev.depenses} /> },
          { label: "Retours B2B", value: MAD(d.retours_total), accent: "#FF9800" },
          { label: "Pertes / Destructions", value: MAD(g.pertes), accent: RED },
          { label: "Marge produits (analyse)", value: MAD(g.benefice_brut) },
          { label: "Bénéfice net", value: MAD(g.benefice_net), accent: g.benefice_net >= 0 ? GREEN : RED, sub: <Evo ev={ev.benefice_net} /> },
          { label: "Marge globale %", value: `${g.marge_pct}%` },
        ]} />
        <ComparisonCard />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">CA & bénéfice par département</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.departements.filter((x) => x.type !== "laboratoire")}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="nom" stroke="#8a8a8a" fontSize={11} tickFormatter={(v) => v.replace("TIRAMESSI ", "")} /><YAxis stroke="#8a8a8a" fontSize={12} />
                <Tooltip contentStyle={tipStyle} formatter={(v) => MAD(v)} /><Legend />
                <Bar dataKey="ca" name="CA" fill={GOLD} radius={[6, 6, 0, 0]} />
                <Bar dataKey="benefice_net" name="Bénéfice net" radius={[6, 6, 0, 0]}>{d.departements.filter((x) => x.type !== "laboratoire").map((x, i) => <Cell key={i} fill={x.benefice_net >= 0 ? GREEN : RED} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <TopProduitsCard />
        </div>
      </>
    );
  } else if (tab === "boutique" || tab === "stand") {
    const dep = tab === "boutique" ? boutiquePrincipale : standFoire;
    exportName = `rapport_${tab}`; exportTitle = `Rapport ${tab === "boutique" ? "Boutique principale" : "Stand Foire"} — ${d.periode.label}`;
    exportCols = [{ header: "Indicateur", value: (r) => r.label }, { header: "Valeur", value: (r) => r.value }];
    exportRows = deptMetrics(dep).map((m) => ({ label: m.label, value: typeof m.value === "string" ? m.value : m.value }));
    body = dep ? (<><Metrics items={deptMetrics(dep)} /><TopProduitsCard title={`Top produits — ${dep.nom.replace("TIRAMESSI ", "")}`} rows={dep.top_vendus} /></>) : <Empty text="Aucune donnée pour ce point de vente." />;
  } else if (tab === "b2b") {
    const b = d.b2b; exportName = "rapport_b2b"; exportTitle = `Rapport Distribution B2B — ${d.periode.label}`;
    const clients = extra.summary?.clients_stats || [];
    exportCols = [{ header: "Client", value: (r) => r.client }, { header: "CA", value: (r) => r.ca }, { header: "Marge", value: (r) => r.marge }, { header: "Créances", value: (r) => r.creances }];
    exportRows = clients;
    body = b ? (
      <>
        <Metrics items={[
          { label: "CA B2B brut", value: MAD(b.ca_brut), accent: GOLD }, { label: "CA net (après retours)", value: MAD(b.ca_net) },
          { label: "Marge brute", value: MAD(b.marge_brut) }, { label: "Bénéfice net", value: MAD(b.benefice_net), accent: b.benefice_net >= 0 ? GREEN : RED },
          { label: "Marge %", value: `${b.marge_pct}%` }, { label: "Quantités livrées", value: b.quantite_livree },
          { label: "Livraisons", value: b.nb_livraisons }, { label: "Retours", value: `${MAD(b.retours_valeur)} (${b.nb_retours})`, accent: "#FF9800" },
          { label: "Dépenses B2B", value: MAD(b.depenses), accent: RED }, { label: "Pertes", value: MAD(b.pertes), accent: RED },
        ]} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Dépenses B2B par catégorie (transport, emballage, remise…)</h3>
            {(b.depenses_categories || []).length === 0 ? <Empty text="Aucune dépense B2B sur la période." /> : (
              <table className="tm-table"><thead><tr><th>Catégorie</th><th className="text-right">Montant</th></tr></thead>
                <tbody>{b.depenses_categories.map((c, i) => <tr key={i}><td>{c.nom}</td><td className="text-right tabular">{MAD(c.montant)}</td></tr>)}</tbody></table>
            )}
          </div>
          <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Clients professionnels (cumul)</h3>
            {clients.length === 0 ? <Empty text="Aucun client." /> : (
              <table className="tm-table"><thead><tr><th>Client</th><th className="text-right">CA</th><th className="text-right">Marge</th><th className="text-right">Créances</th></tr></thead>
                <tbody>{clients.map((c, i) => <tr key={i}><td>{c.client}</td><td className="text-right tabular">{MAD(c.ca)}</td><td className="text-right tabular">{MAD(c.marge)}</td><td className="text-right tabular" style={{ color: c.creances > 0 ? "#FF9800" : "inherit" }}>{MAD(c.creances)}</td></tr>)}</tbody></table>
            )}
          </div>
        </div>
      </>
    ) : <Empty text="Distribution B2B indisponible." />;
  } else if (tab === "platforms") {
    const plats = d.platforms || [];
    const tCA = plats.reduce((a, p) => a + p.ca, 0), tComm = plats.reduce((a, p) => a + p.commission, 0);
    const tNet = plats.reduce((a, p) => a + p.ca_net, 0), tBenef = plats.reduce((a, p) => a + p.benefice, 0), tNb = plats.reduce((a, p) => a + p.nb_ventes, 0);
    exportName = "rapport_glovo"; exportTitle = `Rapport Livraison (plateformes) — ${d.periode.label}`;
    exportCols = [{ header: "Plateforme", value: (p) => p.nom }, { header: "CA brut", value: (p) => p.ca }, { header: "Commission", value: (p) => p.commission }, { header: "CA net", value: (p) => p.ca_net }, { header: "Commandes", value: (p) => p.nb_ventes }, { header: "Panier moyen", value: (p) => p.panier_moyen }, { header: "Bénéfice", value: (p) => p.benefice }, { header: "Marge %", value: (p) => p.marge_pct + "%" }];
    exportRows = plats;
    const platTopMap = {};
    plats.forEach((p) => (p.top_vendus || []).forEach((t) => { platTopMap[t.nom] = (platTopMap[t.nom] || 0) + t.quantite; }));
    const platTop = Object.entries(platTopMap).map(([nom, quantite]) => ({ nom, quantite })).sort((a, b) => b.quantite - a.quantite).slice(0, 8);
    body = (
      <>
        <Metrics items={[
          { label: "CA brut (plateformes)", value: MAD(tCA), accent: "#FFC244" },
          { label: "Commissions totales", value: MAD(tComm), accent: RED },
          { label: "CA net", value: MAD(tNet) },
          { label: "Commandes", value: tNb },
          { label: "Panier moyen", value: MAD(tNb ? tCA / tNb : 0) },
          { label: "Bénéfice estimé", value: MAD(tBenef), accent: tBenef >= 0 ? GREEN : RED },
          { label: "Marge %", value: `${tNet ? Math.round(tBenef / tNet * 1000) / 10 : 0}%` },
        ]} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Détail par plateforme</h3>
            {plats.length === 0 ? <Empty text="Aucune commande sur la période." /> : (
              <table className="tm-table"><thead><tr><th>Plateforme</th><th className="text-right">CA brut</th><th className="text-right">Commission</th><th className="text-right">CA net</th><th className="text-right">Cmd</th><th className="text-right">Panier moy.</th><th className="text-right">Bénéfice</th><th className="text-right">Marge</th></tr></thead>
                <tbody>{plats.map((p, i) => <tr key={i}><td className="font-semibold flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: p.couleur }} />{p.nom}</td><td className="text-right tabular">{MAD(p.ca)}</td><td className="text-right tabular" style={{ color: RED }}>{MAD(p.commission)}</td><td className="text-right tabular">{MAD(p.ca_net)}</td><td className="text-right tabular">{p.nb_ventes}</td><td className="text-right tabular">{MAD(p.panier_moyen)}</td><td className="text-right tabular" style={{ color: p.benefice >= 0 ? GREEN : RED }}>{MAD(p.benefice)}</td><td className="text-right tabular">{p.marge_pct}%</td></tr>)}</tbody></table>
            )}
          </div>
          <TopProduitsCard title="Produits les plus vendus (plateformes)" rows={platTop} />
        </div>
      </>
    );
  } else if (tab === "labo" || tab === "production") {
    const r = extra.prodReport; const labo = d.labo;
    exportName = "rapport_production"; exportTitle = `Rapport Production — ${d.periode.label}`;
    exportCols = [{ header: "Produit", value: (x) => x.produit }, { header: "Quantité", value: (x) => x.quantite }, { header: "Coût total", value: (x) => x.cout_total }, { header: "Coût/unité", value: (x) => x.cout_par_produit }];
    exportRows = r?.par_produit || [];
    body = (
      <>
        <Metrics items={[
          { label: "Coût production (période)", value: MAD(labo?.cout_production || 0), accent: GOLD },
          { label: "Quantité produite", value: labo?.quantite_produite || 0 },
          { label: "Nb productions", value: labo?.nb_productions || 0 },
          { label: "Marge théorique", value: MAD(labo?.marge_theorique || 0), accent: GREEN },
          { label: "Dépenses labo", value: MAD(labo?.depenses || 0), accent: RED },
        ]} />
        {!r ? <Empty text="Aucune donnée de production." /> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Production par produit (cumul)</h3>
              <table className="tm-table"><thead><tr><th>Produit</th><th className="text-right">Qté</th><th className="text-right">Coût total</th><th className="text-right">Coût/u</th></tr></thead>
                <tbody>{r.par_produit.map((p, i) => <tr key={i}><td>{p.produit}</td><td className="text-right tabular">{p.quantite}</td><td className="text-right tabular">{MAD(p.cout_total)}</td><td className="text-right tabular" style={{ color: GOLD }}>{MAD(p.cout_par_produit)}</td></tr>)}</tbody></table>
            </div>
            <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Ingrédients consommés (cumul)</h3>
              <table className="tm-table"><thead><tr><th>Ingrédient</th><th className="text-right">Qté</th><th className="text-right">Coût</th></tr></thead>
                <tbody>{r.ingredients_consommes.map((p, i) => <tr key={i}><td>{p.nom}</td><td className="text-right tabular">{p.quantite} {p.unite}</td><td className="text-right tabular">{MAD(p.cout)}</td></tr>)}</tbody></table>
            </div>
          </div>
        )}
      </>
    );
  } else if (tab === "produits") {
    exportName = "rapport_produits"; exportTitle = "Rapport Produits & marges";
    exportCols = [{ header: "Produit", value: (p) => p.nom }, { header: "Prix vente", value: (p) => p.prix_vente }, { header: "Coût revient", value: (p) => p.cout_matiere }, { header: "Marge DH", value: (p) => p.marge }, { header: "Marge %", value: (p) => p.marge_pct + "%" }];
    exportRows = extra.products;
    body = (
      <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Produits et marges</h3>
        {extra.products.length === 0 ? <Empty text="Aucun produit." /> : (
          <table className="tm-table"><thead><tr><th>Produit</th><th>Catégorie</th><th className="text-right">Prix vente</th><th className="text-right">Coût revient</th><th className="text-right">Marge</th><th className="text-right">Marge %</th></tr></thead>
            <tbody>{extra.products.map((p) => <tr key={p.id}><td className="font-semibold">{p.nom}</td><td className="text-[var(--tm-muted)]">{p.categorie}</td><td className="text-right tabular">{MAD(p.prix_vente)}</td><td className="text-right tabular">{MAD(p.cout_matiere)}</td><td className="text-right tabular" style={{ color: GOLD }}>{MAD(p.marge)}</td><td className="text-right tabular" style={{ color: p.marge_pct >= 50 ? GREEN : "#FF9800" }}>{p.marge_pct}%</td></tr>)}</tbody></table>
        )}
      </div>
    );
  } else if (tab === "depenses") {
    const exps = extra.expenses.filter((e) => inRange(e.date, start, end));
    const byCat = {}; exps.forEach((e) => { byCat[e.categorie || "Divers"] = (byCat[e.categorie || "Divers"] || 0) + (e.montant || 0); });
    exportName = "rapport_depenses"; exportTitle = `Rapport Dépenses — ${d.periode.label}`;
    exportCols = [{ header: "Date", value: (e) => new Date(e.date).toLocaleDateString("fr-FR") }, { header: "Libellé", value: (e) => e.libelle }, { header: "Catégorie", value: (e) => e.categorie }, { header: "Commentaire", value: (e) => e.commentaire || "" }, { header: "Montant", value: (e) => e.montant }];
    exportRows = exps;
    body = (
      <>
        <Metrics items={[{ label: "Dépenses totales", value: MAD(exps.reduce((a, e) => a + (e.montant || 0), 0)), accent: RED }, { label: "Nombre de dépenses", value: exps.length }, ...Object.entries(byCat).slice(0, 6).map(([k, v]) => ({ label: k, value: MAD(v) }))]} />
        <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Détail des dépenses</h3>
          {exps.length === 0 ? <Empty text="Aucune dépense sur la période." /> : (
            <table className="tm-table"><thead><tr><th>Date</th><th>Libellé</th><th>Catégorie</th><th>Commentaire</th><th className="text-right">Montant</th></tr></thead>
              <tbody>{exps.map((e) => <tr key={e.id}><td className="text-[var(--tm-muted)]">{new Date(e.date).toLocaleDateString("fr-FR")}</td><td className="font-semibold">{e.libelle}</td><td>{e.categorie}</td><td className="text-[var(--tm-muted)]">{e.commentaire}</td><td className="text-right tabular" style={{ color: RED }}>{MAD(e.montant)}</td></tr>)}</tbody></table>
          )}
        </div>
      </>
    );
  } else if (tab === "retours") {
    const rets = extra.retours.filter((r) => inRange(r.date, start, end));
    const dests = extra.destructions.filter((x) => inRange(x.date, start, end));
    exportName = "rapport_retours_pertes"; exportTitle = `Rapport Retours & pertes — ${d.periode.label}`;
    exportCols = [{ header: "Date", value: (r) => new Date(r.date).toLocaleDateString("fr-FR") }, { header: "Type", value: (r) => r._type }, { header: "Produit", value: (r) => r.product_nom }, { header: "Quantité", value: (r) => r.quantite }, { header: "Valeur/Coût", value: (r) => r._val }];
    exportRows = [...rets.map((r) => ({ ...r, _type: "Retour B2B", _val: r.valeur })), ...dests.map((x) => ({ ...x, _type: "Destruction", _val: x.cout_total }))];
    body = (
      <>
        <Metrics items={[
          { label: "Retours B2B (valeur)", value: MAD(rets.reduce((a, r) => a + (r.valeur || 0), 0)), accent: "#FF9800" },
          { label: "Nb retours", value: rets.length },
          { label: "Destructions (coût)", value: MAD(dests.reduce((a, x) => a + (x.cout_total || 0), 0)), accent: RED },
          { label: "Nb destructions", value: dests.length },
        ]} />
        <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Retours & pertes détaillés</h3>
          {exportRows.length === 0 ? <Empty text="Aucun retour ni perte sur la période." /> : (
            <table className="tm-table"><thead><tr><th>Date</th><th>Type</th><th>Produit</th><th className="text-right">Qté</th><th className="text-right">Valeur/Coût</th></tr></thead>
              <tbody>{exportRows.map((r, i) => <tr key={i}><td className="text-[var(--tm-muted)]">{new Date(r.date).toLocaleDateString("fr-FR")}</td><td><span className="tm-badge" style={{ background: r._type === "Retour B2B" ? "rgba(255,152,0,.15)" : "rgba(244,67,54,.15)", color: r._type === "Retour B2B" ? "#FF9800" : RED }}>{r._type}</span></td><td>{r.product_nom}</td><td className="text-right tabular">{r.quantite}</td><td className="text-right tabular">{MAD(r._val)}</td></tr>)}</tbody></table>
          )}
        </div>
      </>
    );
  } else if (tab === "stock") {
    exportName = "rapport_stock"; exportTitle = "Rapport Stock ingrédients (Laboratoire)";
    exportCols = [{ header: "Ingrédient", value: (s) => s.nom }, { header: "Quantité", value: (s) => s.quantite }, { header: "Unité", value: (s) => s.unite }, { header: "Seuil", value: (s) => s.seuil_alerte }, { header: "Coût unitaire", value: (s) => s.cout_unitaire }, { header: "Valeur", value: (s) => (s.quantite * s.cout_unitaire).toFixed(2) }];
    exportRows = extra.stock;
    const totalVal = extra.stock.reduce((a, s) => a + (s.quantite || 0) * (s.cout_unitaire || 0), 0);
    const critiques = extra.stock.filter((s) => (s.quantite || 0) <= (s.seuil_alerte || 0));
    body = (
      <>
        <Metrics items={[{ label: "Valeur du stock", value: MAD(totalVal), accent: GOLD }, { label: "Ingrédients", value: extra.stock.length }, { label: "Sous le seuil", value: critiques.length, accent: critiques.length ? RED : GREEN }]} />
        <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Stock ingrédients (Laboratoire)</h3>
          {extra.stock.length === 0 ? <Empty text="Stock vide." /> : (
            <table className="tm-table"><thead><tr><th>Ingrédient</th><th className="text-right">Qté</th><th className="text-right">Seuil</th><th className="text-right">Coût unit.</th><th className="text-right">Valeur</th><th>État</th></tr></thead>
              <tbody>{extra.stock.map((s) => { const crit = (s.quantite || 0) <= (s.seuil_alerte || 0); return <tr key={s.id}><td className="font-semibold">{s.nom}</td><td className="text-right tabular">{s.quantite} {s.unite}</td><td className="text-right tabular text-[var(--tm-muted)]">{s.seuil_alerte} {s.unite}</td><td className="text-right tabular">{MAD(s.cout_unitaire)}</td><td className="text-right tabular">{MAD((s.quantite || 0) * (s.cout_unitaire || 0))}</td><td><span className="tm-badge" style={crit ? { background: "rgba(244,67,54,.15)", color: RED } : { background: "rgba(76,175,80,.15)", color: GREEN }}>{crit ? "Faible" : "OK"}</span></td></tr>; })}</tbody></table>
          )}
        </div>
      </>
    );
  } else if (tab === "freezer") {
    const fz = d.freezer || {};
    const batches = extra.freezer || [];
    exportName = "rapport_congelateur"; exportTitle = "Rapport Stock Congélateur";
    exportCols = [{ header: "Lot", value: (b) => b.numero_lot }, { header: "Produit", value: (b) => b.product_nom }, { header: "Catégorie", value: (b) => b.categorie }, { header: "Qté initiale", value: (b) => b.quantite_initiale }, { header: "Qté restante", value: (b) => b.quantite_restante }, { header: "Statut", value: (b) => b.statut }];
    exportRows = batches;
    body = (
      <>
        <Metrics items={[
          { label: "Produits congelés", value: fz.nb_produits || 0, accent: "#4FC3F7" },
          { label: "Valeur estimée", value: MAD(fz.valeur || 0), accent: GOLD },
          { label: "Lots disponibles", value: fz.lots_disponibles || 0 },
          { label: "Lots épuisés", value: fz.lots_epuises || 0 },
          { label: "Sorties (période)", value: `${fz.sorties_qte || 0} (${fz.sorties_nb || 0})` },
          { label: "Lots anciens (>21j)", value: (fz.alertes_anciens || []).length, accent: (fz.alertes_anciens || []).length ? "#FF9800" : GREEN },
        ]} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Produits congelés par catégorie</h3>
            {(fz.par_categorie || []).length === 0 ? <Empty text="Congélateur vide." /> : (
              <table className="tm-table"><thead><tr><th>Catégorie</th><th className="text-right">Quantité</th></tr></thead>
                <tbody>{fz.par_categorie.map((c, i) => <tr key={i}><td>{c.nom}</td><td className="text-right tabular">{c.quantite}</td></tr>)}</tbody></table>
            )}
          </div>
          <div className="tm-card p-5"><h3 className="font-display text-xl mb-4">Lots au congélateur</h3>
            {batches.length === 0 ? <Empty text="Aucun lot." /> : (
              <table className="tm-table"><thead><tr><th>Lot</th><th>Produit</th><th className="text-right">Restant</th><th>Statut</th></tr></thead>
                <tbody>{batches.map((b) => <tr key={b.id}><td className="tabular">{b.numero_lot}</td><td>{b.product_nom}</td><td className="text-right tabular" style={{ color: "#4FC3F7" }}>{b.quantite_restante}</td><td><span className="tm-badge" style={b.statut === "epuise" ? { background: "rgba(244,67,54,.15)", color: RED } : { background: "rgba(79,195,247,.15)", color: "#4FC3F7" }}>{b.statut}</span></td></tr>)}</tbody></table>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div>
      <PageHeader title="Rapports" subtitle={`Analyse complète — ${d.periode.label}`}>
        <ExportButtons onExcel={() => exportExcel(exportName, exportCols, exportRows)} onPDF={() => exportPDF(exportTitle, exportCols, exportRows)} />
      </PageHeader>

      <PeriodFilter value={q} onChange={onPeriod} />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="report-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`report-tab-${t.key}`}
            className="px-3 py-1.5 rounded-md text-sm font-semibold transition-colors"
            style={tab === t.key ? { background: GOLD, color: "#000" } : { background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>
            {t.label}
          </button>
        ))}
      </div>

      {body}
    </div>
  );
}
